import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { 
  Search, ChevronLeft, ChevronRight, User, Mail, 
  CreditCard, Calendar, Gift, Edit2, Eye, Filter
} from 'lucide-react';
import { adminFetch } from './AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const AdminUsers = () => {
  const { adminToken } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [giftCredits, setGiftCredits] = useState({ show: false, userId: '', amount: 10, reason: '' });
  const [filterType, setFilterType] = useState('all'); // all, real, test
  const limit = 20;

  // Test account patterns
  const isTestAccount = (email) => {
    if (!email) return false;
    const testPatterns = [
      /@test\.com$/i,
      /@mandala-test\.com$/i,
      /@mandalamuse-test\.com$/i,
      /^test[_-]/i,
      /^testing[_-]/i,
      /_test@/i,
      /test\d+@/i,
    ];
    return testPatterns.some(pattern => pattern.test(email));
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
        skip: page * limit, 
        limit: limit,
        filter_type: filterType  // Send filter to backend
      });
      if (search) params.append('search', search);
      
      console.log('Fetching users with token:', adminToken ? 'present' : 'missing');
      const data = await adminFetch(`/admin/users?${params}`, {}, adminToken);
      console.log('Users API response:', data);
      
      if (!data || !data.users) {
        console.error('Invalid response structure:', data);
        setUsers([]);
        setTotal(0);
        return;
      }
      
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      console.error('Fetch users error:', err);
      toast.error(err.message || 'Failed to fetch users');
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, adminToken, filterType]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchUsers();
  };

  const handleFilterChange = (value) => {
    setFilterType(value);
    setPage(0);
  };

  const handleGiftCredits = async () => {
    try {
      await adminFetch('/admin/gift-credits', {
        method: 'POST',
        body: JSON.stringify({
          user_id: giftCredits.userId,
          credits: giftCredits.amount,
          reason: giftCredits.reason || 'Admin gift'
        })
      }, adminToken);
      
      toast.success(`Gifted ${giftCredits.amount} credits`);
      setGiftCredits({ show: false, userId: '', amount: 10, reason: '' });
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Users</h1>
          <p className="text-gray-400 mt-1">{total} total users</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-wrap gap-3 items-center">
        <form onSubmit={handleSearch} className="flex gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="pl-10 bg-gray-800 border-gray-700 text-white"
              data-testid="user-search-input"
            />
          </div>
          <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
            Search
          </Button>
        </form>
        
        {/* Filter Dropdown */}
        <div className="flex items-center gap-2">
          <Filter size={18} className="text-gray-400" />
          <Select value={filterType} onValueChange={handleFilterChange}>
            <SelectTrigger className="w-[160px] bg-gray-800 border-gray-700 text-white">
              <SelectValue placeholder="Filter users" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="all" className="text-white hover:bg-gray-700">All Users</SelectItem>
              <SelectItem value="real" className="text-white hover:bg-gray-700">Real Users Only</SelectItem>
              <SelectItem value="test" className="text-white hover:bg-gray-700">Test Accounts Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">User</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Email</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Credits</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Joined</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center">
                    <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-700/30" data-testid={`user-row-${user.user_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-600/20 rounded-full flex items-center justify-center">
                          {user.picture ? (
                            <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                          ) : (
                            <User className="w-5 h-5 text-emerald-500" />
                          )}
                        </div>
                        <span className="text-white font-medium">{user.name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-300">{user.email}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-600/20 text-purple-400 rounded-full text-sm">
                        <CreditCard size={14} />
                        {user.credits || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-sm">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setGiftCredits({ show: true, userId: user.user_id, amount: 10, reason: '' })}
                          className="p-2 hover:bg-gray-600 rounded-lg text-yellow-400 transition-colors"
                          title="Gift Credits"
                        >
                          <Gift size={18} />
                        </button>
                        <Link
                          to={`/admin/users/${user.user_id}`}
                          className="p-2 hover:bg-gray-600 rounded-lg text-emerald-400 transition-colors"
                          title="View Details"
                        >
                          <Eye size={18} />
                        </Link>
                        <Link
                          to={`/admin/artworks?user_id=${user.user_id}`}
                          className="p-2 hover:bg-gray-600 rounded-lg text-blue-400 transition-colors"
                          title="View Artworks"
                        >
                          <Edit2 size={18} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-400">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p - 1)}
                disabled={page === 0}
                className="border-gray-600"
              >
                <ChevronLeft size={16} />
              </Button>
              <span className="text-gray-400 text-sm px-2">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
                className="border-gray-600"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Gift Credits Modal */}
      {giftCredits.show && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-white mb-4">Gift Credits</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Amount</label>
                <Input
                  type="number"
                  min="1"
                  value={giftCredits.amount}
                  onChange={(e) => setGiftCredits(g => ({ ...g, amount: parseInt(e.target.value) || 0 }))}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Reason (optional)</label>
                <Input
                  value={giftCredits.reason}
                  onChange={(e) => setGiftCredits(g => ({ ...g, reason: e.target.value }))}
                  placeholder="e.g., Promotional bonus"
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button 
                variant="outline" 
                onClick={() => setGiftCredits({ show: false, userId: '', amount: 10, reason: '' })}
                className="border-gray-600"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleGiftCredits}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Gift Credits
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
